import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9occh4it35ii3jdm5pg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string') {
          body.append(key, value);
        }
      });

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
        setFormError(serverMsg);
        setFormStatus('error');
      } else {
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const stats = [
    { value: '5000+', label: '影视资源' },
    { value: '800万+', label: '活跃用户' },
    { value: '99.9%', label: '在线率' },
    { value: '24/7', label: '全天候服务' },
  ];

  const testimonials = [
    {
      name: '深夜追剧人',
      role: '资深用户',
      content: '自从发现了夜猫影院，我每晚的追剧时间都变得无比惬意。高清流畅，资源丰富，强烈推荐给所有夜猫子！',
      rating: 5,
    },
    {
      name: '电影爱好者阿强',
      role: 'VIP用户',
      content: '夜猫影院的资源真的很全，基本上我想看的电影都能找到。夜间模式对眼睛也很友好，熬夜看电影也不累。',
      rating: 5,
    },
    {
      name: '宅家观影达人',
      role: '忠实用户',
      content: '画质好、更新快、不卡顿，这是我最喜欢的三点。通宵追剧再也不怕缓冲了，夜猫影院真的很懂我们。',
      rating: 5,
    },
  ];

  useEffect(() => {
    const ldJson = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://www.yemaocinema.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: '关于我们',
              item: 'https://www.yemaocinema.com/about',
            },
          ],
        },
        {
          '@type': 'AboutPage',
          name: '关于夜猫影院',
          url: 'https://www.yemaocinema.com/about',
          description: '夜猫影院 - 专为夜猫子打造的夜间影视平台，了解我们的品牌故事、口碑评价和联系方式。',
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(ldJson);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-16 min-h-screen">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4 border-b border-background-700/30">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
              <span className="text-foreground-700">/</span>
              <span className="text-foreground-300">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cozy%20home%20theater%20room%20at%20night%20with%20large%20projector%20screen%20displaying%20cinematic%20content%2C%20comfortable%20sofa%2C%20soft%20warm%20golden%20ambient%20lighting%2C%20popcorn%20bowl%20on%20table%2C%20night%20atmosphere%2C%20artistic%20interior%20photography%2C%20warm%20tones%2C%20cozy%20vibes&width=1600&height=800&seq=about-hero-01&orientation=landscape"
            alt="夜猫影院品牌介绍"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/50 via-background-950/30 to-background-950"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-50 mb-3">
                关于夜猫影院
              </h1>
              <p className="text-foreground-400 text-sm md:text-base max-w-lg mx-auto">
                专为夜猫子打造的夜间影视平台
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center p-5 rounded-lg bg-background-800/30 border border-background-700/20">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading mb-1">
                    {stat.value}
                  </div>
                  <div className="text-foreground-500 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-900/50">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-6">
                品牌故事
              </h2>
              <div className="space-y-4 text-foreground-500 text-sm leading-relaxed">
                <p>
                  夜猫影院诞生于一个深夜——当一群热爱影视的夜猫子发现，深夜想找一部好电影来看实在太难了。要么资源不全，要么画质模糊，要么卡顿到让人崩溃。
                </p>
                <p>
                  于是我们决定：为所有夜猫子打造一个专属的夜间影视平台。我们投入大量资源建设智能CDN加速网络，精选全球优质影视内容，优化夜间观影体验的每一个细节。
                </p>
                <p>
                  今天，夜猫影院已经成为数百万夜猫子的深夜观影首选。无论是熬夜追热播剧，还是通宵刷经典电影，夜猫影院都陪你度过每一个不眠之夜。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 text-center mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="p-5 rounded-lg bg-background-800/30 border border-background-700/20"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-primary-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-foreground-400 text-sm leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <span className="text-primary-400 text-xs font-bold">{t.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-foreground-300 text-xs font-medium">{t.name}</div>
                      <div className="text-foreground-600 text-xs">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-900/50">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 text-center mb-3">
              联系我们
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-8">
              有任何问题或建议？欢迎给我们留言，我们会尽快回复。
            </p>

            <form
              id="contact-form"
              data-readdy-form=""
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-foreground-400 text-xs font-medium mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-800 border border-background-600/50 text-foreground-200 text-sm focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-colors placeholder:text-foreground-700"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-foreground-400 text-xs font-medium mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-800 border border-background-600/50 text-foreground-200 text-sm focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-colors placeholder:text-foreground-700"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-foreground-400 text-xs font-medium mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-800 border border-background-600/50 text-foreground-200 text-sm focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-colors placeholder:text-foreground-700"
                  placeholder="请输入留言主题"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-foreground-400 text-xs font-medium mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-800 border border-background-600/50 text-foreground-200 text-sm focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-colors resize-none placeholder:text-foreground-700"
                  placeholder="请输入您的留言内容（500字以内）"
                ></textarea>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-anti-spam"
              />

              <div className="text-center">
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-send-plane-line"></i>
                  </div>
                  发送留言
                </button>
              </div>

              {formStatus === 'success' && (
                <div className="text-center p-3 rounded-lg bg-accent-500/10 border border-accent-500/20">
                  <p className="text-accent-400 text-sm">感谢您的留言！我们会尽快与您联系。</p>
                </div>
              )}
              {formStatus === 'error' && formError && (
                <div className="text-center p-3 rounded-lg bg-accent-500/10 border border-accent-500/20">
                  <p className="text-accent-400 text-sm">{formError}</p>
                </div>
              )}
            </form>
          </div>
        </section>

        {/* Contact Info */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="text-center p-5 rounded-lg bg-background-800/30 border border-background-700/20">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
                  <i className="ri-mail-line text-primary-400 text-lg"></i>
                </div>
                <h4 className="text-foreground-300 text-sm font-medium mb-1">电子邮箱</h4>
                <p className="text-foreground-500 text-xs">contact@yemaocinema.com</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-800/30 border border-background-700/20">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
                  <i className="ri-phone-line text-primary-400 text-lg"></i>
                </div>
                <h4 className="text-foreground-300 text-sm font-medium mb-1">客服热线</h4>
                <p className="text-foreground-500 text-xs">400-888-夜猫</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-800/30 border border-background-700/20">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
                  <i className="ri-map-pin-line text-primary-400 text-lg"></i>
                </div>
                <h4 className="text-foreground-300 text-sm font-medium mb-1">公司地址</h4>
                <p className="text-foreground-500 text-xs">北京市朝阳区夜猫大厦</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}